/**
 * @copyright 2023 Honda Motor Co., Ltd. All rights reserved.
 */

import { FC } from "react";
import { ConfirmDialog } from "components/Common/ConfirmDialog";
import { ManageUrgencyType, NotificationListEventsInner } from "client";
import { useTranslation } from "react-i18next";
import InformationIcon from "@mui/icons-material/Info";
import WarningIcon from "@mui/icons-material/Warning";
import { Stack, Typography } from "@mui/material";
import moment from "moment";
import { Const } from "const";

interface Props {
  open: boolean;
  notification: NotificationListEventsInner;
  onClose: () => void;
}

export const NotificationDetailDialog: FC<Props> = ({
  open,
  onClose,
  notification,
}) => {
  const { t } = useTranslation(["monitor", "notification"]);
  return (
    <ConfirmDialog
      open={open}
      title={
        <Stack direction="row" alignItems="center" gap={2}>
          {notification?.urgency === ManageUrgencyType.Urgent ? (
            <WarningIcon color="error" />
          ) : (
            <InformationIcon />
          )}
          <Typography variant="inherit">
            {t(`AWV-UI-SUBJECT-${notification.eventId!}`, {
              ns: "notification",
            })}
          </Typography>
        </Stack>
      }
      message={
        <Stack
          width={"100%"}
          direction="column"
          textAlign="left"
          alignItems="flex-start"
          justifyContent="flex-start"
        >
          <Typography variant="inherit">
            {moment(notification.notifiedAt).format(Const.dateTimeFormat)}
          </Typography>
          <Typography variant="inherit">
            {`[${notification.eventId}]`}
          </Typography>
          <Typography variant="inherit" style={{ whiteSpace: "pre-wrap" }}>
            {t(`AWV-UI-BODY-${notification.eventId!}`, {
              ns: "notification",
              frameTypeAndNumber: notification.frameTypeAndNumber,
              taskId: notification.taskIdDisp,
            })}
          </Typography>
        </Stack>
      }
      onPositiveButtonClick={onClose}
      positiveButtonText={t("AWVUI-MSG22")}
    />
  );
};
